import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Home, LogOut, Plus, Trash2, Calendar, Percent, ToggleLeft, ToggleRight, 
  Gift, Mail, Image, Euro, Clock, Tag, Send, Sparkles, Eye
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useTheme } from '@/context/ThemeContext';
import ThemeToggle from '@/components/ThemeToggle';
import ImageUploader from '@/components/ImageUploader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

export default function AdminOffers({ user, onLogout }) {
  const { isDark } = useTheme();
  
  // Theme classes
  const t = {
    bg: isDark ? 'bg-gradient-to-br from-[#0a0a0a] via-[#1a1a2e] to-[#16213e]' : 'bg-gradient-to-br from-[#F9F7F2] via-[#FFFFFF] to-[#F5F3EE]',
    card: isDark ? 'bg-white/5 backdrop-blur-xl border-white/10' : 'bg-white shadow-lg border-gray-200',
    text: isDark ? 'text-white' : 'text-gray-900',
    textSecondary: isDark ? 'text-white/60' : 'text-gray-600',
    textMuted: isDark ? 'text-white/40' : 'text-gray-400',
    input: isDark ? 'bg-white/5 border-white/10 text-white placeholder-white/30' : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400',
    header: isDark ? 'bg-black/20 backdrop-blur-xl border-white/10' : 'bg-white/80 backdrop-blur-xl border-gray-200',
    navLink: isDark ? 'text-white/60 hover:text-white' : 'text-gray-600 hover:text-gray-900',
    modal: isDark ? 'bg-gradient-to-br from-[#1a1a2e] to-[#16213e] border-white/10' : 'bg-white border-gray-200 shadow-2xl',
    glow1: isDark ? 'bg-[#4A5D23]/20' : 'bg-[#4A5D23]/5',
    glow2: isDark ? 'bg-[#C5A059]/10' : 'bg-[#C5A059]/5',
  };
  
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [sendingNotification, setSendingNotification] = useState(null);
  const [creating, setCreating] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    discount_percentage: '',
    price_per_kg: '',
    valid_until: '',
    image_url: ''
  });

  useEffect(() => {
    fetchOffers();
  }, []);

  const fetchOffers = async () => {
    try {
      const response = await axios.get(`${API}/admin/offers/all`, getAuthHeaders());
      setOffers(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento delle offerte');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.title || !formData.description) {
      toast.error('Inserisci titolo e descrizione');
      return;
    }

    setCreating(true);
    try {
      await axios.post(`${API}/admin/offers`, {
        ...formData,
        discount_percentage: formData.discount_percentage ? parseFloat(formData.discount_percentage) : null,
        price_per_kg: formData.price_per_kg ? parseFloat(formData.price_per_kg) : null
      }, getAuthHeaders());
      
      toast.success('Offerta creata!');
      setShowCreateModal(false);
      setFormData({ title: '', description: '', discount_percentage: '', price_per_kg: '', valid_until: '', image_url: '' });
      fetchOffers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella creazione');
    } finally {
      setCreating(false);
    }
  };

  const handleToggle = async (offerId, currentStatus) => {
    try {
      await axios.put(`${API}/admin/offers/${offerId}/toggle`, {}, getAuthHeaders());
      toast.success(currentStatus ? 'Offerta disattivata' : 'Offerta attivata');
      fetchOffers();
    } catch (error) {
      toast.error('Errore nell\'aggiornamento');
    }
  };

  const handleDelete = async (offerId) => {
    if (!window.confirm('Eliminare questa offerta?')) return;
    
    try {
      await axios.delete(`${API}/admin/offers/${offerId}`, getAuthHeaders());
      toast.success('Offerta eliminata');
      fetchOffers();
    } catch (error) {
      toast.error('Errore durante l\'eliminazione');
    }
  };

  const handleSendNotification = async (offer) => {
    if (!window.confirm(`Inviare email a tutti i clienti per "${offer.title}"?`)) return;
    
    setSendingNotification(offer.id);
    try {
      const response = await axios.post(
        `${API}/admin/offers/${offer.id}/notify`,
        {},
        getAuthHeaders()
      );
      toast.success(response.data.message);
    } catch (error) {
      toast.error('Errore nell\'invio delle notifiche');
    } finally {
      setSendingNotification(null);
    }
  };

  if (loading) {
    return (
      <div className={`min-h-screen ${t.bg} flex items-center justify-center`}>
        <div className={`${t.text} text-xl flex items-center gap-3`}>
          <div className="w-8 h-8 border-4 border-[#C5A059] border-t-transparent rounded-full animate-spin"></div>
          Caricamento...
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${t.bg}`}>
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className={`absolute top-20 right-20 w-72 h-72 ${t.glow1} rounded-full blur-3xl animate-pulse`}></div>
        <div className={`absolute bottom-20 left-20 w-96 h-96 ${t.glow2} rounded-full blur-3xl animate-pulse`} style={{ animationDelay: '1s' }}></div>
      </div>

      {/* Header */}
      <header className={`relative z-10 border-b ${t.header}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-6">
            <Link to="/admin" className={`flex items-center gap-2 ${t.navLink} transition-colors`}>
              <Home size={20} />
              <span className="hidden sm:inline text-sm">Dashboard</span>
            </Link>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#4A5D23] to-[#2E3A16] flex items-center justify-center shadow-lg shadow-[#4A5D23]/20">
                <Gift className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className={`text-2xl font-bold ${t.text}`}>Offerte Speciali</h1>
                <p className="text-sm text-[#7CB342]">Gestisci promozioni e sconti</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-6 py-2.5 bg-gradient-to-r from-[#4A5D23] to-[#2E3A16] text-white rounded-xl font-semibold flex items-center gap-2 hover:shadow-lg hover:shadow-[#4A5D23]/30 transition-all"
            >
              <Plus size={18} />
              Nuova Offerta
            </button>
            <ThemeToggle />
            <button onClick={onLogout} className={`p-2 rounded-lg ${t.textSecondary} hover:${isDark ? 'bg-white/10' : 'bg-gray-100'} transition-colors`}>
              <LogOut size={20} />
            </button>
          </div>
        </div>
      </header>

      <main className="relative z-10 max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <div className={`rounded-2xl ${t.card} border p-6`}>
            <p className={`text-4xl font-bold ${t.text} mb-1`}>{offers.length}</p>
            <p className={`${t.textSecondary} text-sm`}>Totale Offerte</p>
          </div>
          <div className="rounded-2xl bg-[#4A5D23]/20 border border-[#4A5D23]/30 p-6">
            <p className="text-4xl font-bold text-[#7CB342] mb-1">{offers.filter(o => o.active).length}</p>
            <p className={`${t.textSecondary} text-sm`}>Attive</p>
          </div>
          <div className={`rounded-2xl ${t.card} border p-6`}>
            <p className={`text-4xl font-bold ${isDark ? 'text-white/50' : 'text-gray-400'} mb-1`}>{offers.filter(o => !o.active).length}</p>
            <p className={`${t.textSecondary} text-sm`}>Disattivate</p>
          </div>
        </div>

        {/* Offers Grid */}
        {offers.length === 0 ? (
          <div className={`rounded-2xl ${t.card} border p-16 text-center`}>
            <div className={`w-24 h-24 mx-auto mb-6 rounded-full ${isDark ? 'bg-white/5' : 'bg-gray-100'} flex items-center justify-center`}>
              <Gift className={`w-12 h-12 ${isDark ? 'text-white/20' : 'text-gray-300'}`} />
            </div>
            <p className={`${t.textSecondary} text-lg mb-4`}>Nessuna offerta ancora</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-6 py-3 bg-gradient-to-r from-[#4A5D23] to-[#2E3A16] text-white rounded-xl font-semibold"
            >
              Crea la prima offerta
            </button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {offers.map(offer => (
              <div 
                key={offer.id} 
                className={`rounded-2xl backdrop-blur-xl border overflow-hidden transition-all hover:-translate-y-1 ${
                  offer.active 
                    ? `${t.card} hover:border-[#4A5D23]/50` 
                    : isDark ? 'bg-white/[0.02] border-white/5 opacity-60' : 'bg-gray-50 border-gray-200 opacity-60'
                }`}
              >
                {/* Image or Placeholder */}
                <div className="h-40 bg-gradient-to-br from-[#5D1822]/30 to-[#C5A059]/20 flex items-center justify-center relative overflow-hidden">
                  {offer.image_url ? (
                    <img src={offer.image_url} alt={offer.title} className="w-full h-full object-cover" />
                  ) : (
                    <Tag className={`w-16 h-16 ${isDark ? 'text-white/20' : 'text-gray-300'}`} />
                  )}
                  {/* Status Badge */}
                  <div className={`absolute top-3 right-3 px-3 py-1 rounded-full text-xs font-medium ${
                    offer.active 
                      ? 'bg-[#4A5D23] text-white' 
                      : isDark ? 'bg-white/20 text-white/60' : 'bg-gray-200 text-gray-500'
                  }`}>
                    {offer.active ? '✓ Attiva' : 'Disattivata'}
                  </div>
                </div>

                {/* Content */}
                <div className="p-5">
                  <h3 className={`text-lg font-bold ${t.text} mb-2`}>{offer.title}</h3>
                  <p className={`${t.textSecondary} text-sm mb-4 line-clamp-2`}>{offer.description}</p>

                  {/* Price/Discount Info */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {offer.discount_percentage && (
                      <span className="px-3 py-1 rounded-full bg-[#C5A059]/20 text-[#C5A059] text-sm font-medium flex items-center gap-1">
                        <Percent size={14} />
                        -{offer.discount_percentage}%
                      </span>
                    )}
                    {offer.price_per_kg && (
                      <span className="px-3 py-1 rounded-full bg-[#4A5D23]/20 text-[#7CB342] text-sm font-medium flex items-center gap-1">
                        <Euro size={14} />
                        €{offer.price_per_kg}/kg
                      </span>
                    )}
                    {offer.valid_until && (
                      <span className={`px-3 py-1 rounded-full ${isDark ? 'bg-white/10 text-white/60' : 'bg-gray-100 text-gray-500'} text-sm font-medium flex items-center gap-1`}>
                        <Calendar size={14} />
                        {new Date(offer.valid_until).toLocaleDateString('it-IT')}
                      </span>
                    )}
                  </div>

                  {/* Actions */}
                  <div className={`flex items-center gap-2 pt-4 border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                    <button
                      onClick={() => handleSendNotification(offer)}
                      disabled={sendingNotification === offer.id}
                      className="flex-1 px-3 py-2 bg-[#4A5D23]/20 text-[#7CB342] rounded-lg text-sm font-medium flex items-center justify-center gap-2 hover:bg-[#4A5D23]/30 transition-colors disabled:opacity-50"
                    >
                      {sendingNotification === offer.id ? (
                        'Invio...'
                      ) : (
                        <>
                          <Mail size={16} />
                          Notifica
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => handleToggle(offer.id, offer.active)}
                      className={`p-2 rounded-lg ${isDark ? 'bg-white/5 hover:bg-white/10' : 'bg-gray-100 hover:bg-gray-200'} transition-colors`}
                      title={offer.active ? 'Disattiva' : 'Attiva'}
                    >
                      {offer.active ? (
                        <ToggleRight className="w-5 h-5 text-[#7CB342]" />
                      ) : (
                        <ToggleLeft className={`w-5 h-5 ${t.textMuted}`} />
                      )}
                    </button>
                    <button
                      onClick={() => handleDelete(offer.id)}
                      className={`p-2 rounded-lg ${isDark ? 'bg-white/5 hover:bg-red-500/20 text-white/40' : 'bg-gray-100 hover:bg-red-100 text-gray-400'} hover:text-red-400 transition-colors`}
                      title="Elimina"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`${t.modal} border rounded-3xl max-w-lg w-full p-8 shadow-2xl max-h-[90vh] overflow-y-auto`}>
            <h3 className={`text-2xl font-bold ${t.text} mb-6 flex items-center gap-3`}>
              <div className="w-10 h-10 rounded-xl bg-[#4A5D23]/30 flex items-center justify-center">
                <Plus size={20} className="text-[#7CB342]" />
              </div>
              Nuova Offerta
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className={`block ${t.textSecondary} text-sm mb-2`}>Titolo *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Es: Offerta Weekend"
                  className={`w-full px-4 py-3 ${t.input} border rounded-xl focus:outline-none focus:border-[#4A5D23]/50`}
                />
              </div>
              
              <div>
                <label className={`block ${t.textSecondary} text-sm mb-2`}>Descrizione *</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Descrivi l'offerta..."
                  rows="3"
                  className={`w-full px-4 py-3 ${t.input} border rounded-xl focus:outline-none focus:border-[#4A5D23]/50 resize-none`}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={`block ${t.textSecondary} text-sm mb-2`}>Sconto %</label>
                  <input
                    type="number"
                    value={formData.discount_percentage}
                    onChange={(e) => setFormData({ ...formData, discount_percentage: e.target.value })}
                    placeholder="Es: 20"
                    className={`w-full px-4 py-3 ${t.input} border rounded-xl focus:outline-none focus:border-[#4A5D23]/50`}
                  />
                </div>
                <div>
                  <label className={`block ${t.textSecondary} text-sm mb-2`}>Prezzo €/kg</label>
                  <input
                    type="number"
                    value={formData.price_per_kg}
                    onChange={(e) => setFormData({ ...formData, price_per_kg: e.target.value })}
                    placeholder="Es: 12.50"
                    step="0.01"
                    className={`w-full px-4 py-3 ${t.input} border rounded-xl focus:outline-none focus:border-[#4A5D23]/50`}
                  />
                </div>
              </div>

              <div>
                <label className={`block ${t.textSecondary} text-sm mb-2`}>Valida Fino Al</label>
                <input
                  type="date"
                  value={formData.valid_until}
                  onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
                  className={`w-full px-4 py-3 ${t.input} border rounded-xl focus:outline-none focus:border-[#4A5D23]/50`}
                />
              </div>

              <ImageUploader
                label="Immagine Offerta (opzionale)"
                currentImage={formData.image_url}
                onImageChange={(url) => setFormData({ ...formData, image_url: url })}
                isDark={isDark}
              />
            </div>
            
            <div className="flex gap-3 mt-8">
              <button
                onClick={() => setShowCreateModal(false)}
                className={`flex-1 px-4 py-3 ${isDark ? 'bg-white/10 text-white hover:bg-white/20' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'} rounded-xl font-medium`}
              >
                Annulla
              </button>
              <button
                onClick={handleCreate}
                disabled={creating || !formData.title || !formData.description}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-[#4A5D23] to-[#2E3A16] text-white rounded-xl font-semibold disabled:opacity-50"
              >
                {creating ? 'Creazione...' : 'Crea Offerta'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
